import { useState, useCallback } from "react";
import { useCspApi } from "@perfectserve/http-ps";
export const useCallFinder = () => {
    const { get } = useCspApi();
    const [interactions, setInteractions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetchCount, setLastFetchCount] = useState(0);
    const MAX_ACCOUNTS_PER_BATCH = 50;
    const search = useCallback(async (filters) => {
        const { datetime, selectedCallOutcomes, contextSelection, excludeHangupsAndDisconnects, skip = 0, top = 100, append = false } = filters;
        if (!datetime) {
            setError("Please select a date and time to search");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const accountIds = contextSelection.accountIds || [];
            const userIds = contextSelection.userIds || [];
            const needsBatching = accountIds.length > MAX_ACCOUNTS_PER_BATCH;
            if (needsBatching) {
                console.log(`Batching request: ${accountIds.length} accounts split into batches of ${MAX_ACCOUNTS_PER_BATCH}`);
                const allResults = [];
                const accountBatches = [];
                for (let i = 0; i < accountIds.length; i += MAX_ACCOUNTS_PER_BATCH) {
                    accountBatches.push(accountIds.slice(i, i + MAX_ACCOUNTS_PER_BATCH));
                }
                for (let i = 0; i < accountBatches.length; i++) {
                    const accountBatch = accountBatches[i];
                    if (!accountBatch)
                        continue;
                    console.log(`Fetching batch ${i + 1}/${accountBatches.length} (${accountBatch.length} accounts)`);
                    const batchResults = await executeSingleSearch({
                        datetime,
                        selectedCallOutcomes,
                        excludeHangupsAndDisconnects,
                        accountIds: accountBatch,
                        userIds,
                        get,
                        skip,
                        top,
                    });
                    allResults.push(...batchResults);
                }
                const uniqueResults = Array.from(new Map(allResults.map((item) => [item.Id, item])).values());
                uniqueResults.sort((a, b) => {
                    const dateA = new Date(a.InteractionStartDateUtc || 0).getTime();
                    const dateB = new Date(b.InteractionStartDateUtc || 0).getTime();
                    return dateB - dateA;
                });
                const limitedResults = uniqueResults.slice(0, top);
                console.log(`Combined ${allResults.length} results, ${uniqueResults.length} unique, returning top ${limitedResults.length}`);
                setLastFetchCount(limitedResults.length);
                if (append) {
                    setInteractions((prev) => [...prev, ...limitedResults]);
                }
                else {
                    setInteractions(limitedResults);
                }
            }
            else {
                const results = await executeSingleSearch({
                    datetime,
                    selectedCallOutcomes,
                    excludeHangupsAndDisconnects,
                    accountIds,
                    userIds,
                    get,
                    skip,
                    top,
                });
                setLastFetchCount(results.length);
                if (append) {
                    setInteractions((prev) => [...prev, ...results]);
                }
                else {
                    setInteractions(results);
                }
            }
            setError(null);
        }
        catch (err) {
            console.error("Call Finder search error:", err);
            setError("Failed to search interactions. Please try again.");
            setInteractions([]);
        }
        finally {
            setLoading(false);
        }
    }, [get]);
    const executeSingleSearch = async ({ datetime, selectedCallOutcomes, excludeHangupsAndDisconnects, accountIds, userIds, get, skip, top, }) => {
        const filterParts = [];
        const formattedDateTime = datetime.toISOString();
        filterParts.push(`InteractionStartDateUtc ge ${formattedDateTime}`);
        if (selectedCallOutcomes && selectedCallOutcomes.length > 0) {
            const outcomeList = selectedCallOutcomes.map((outcome) => `'${outcome}'`).join(",");
            filterParts.push(`InteractionFinalAction in (${outcomeList})`);
        }
        if (excludeHangupsAndDisconnects) {
            filterParts.push(`InteractionFinalAction ne 'HangUp' and InteractionFinalAction ne 'Hangup' and InteractionFinalAction ne 'Disconnect' and InteractionFinalAction ne 'Disconnected'`);
        }
        if (accountIds.length > 0) {
            const accountList = accountIds.map((id) => `'${id}'`).join(",");
            filterParts.push(`ToFilterObjectAccountId in (${accountList})`);
        }
        if (userIds.length > 0) {
            const userList = userIds.map((id) => `'${id}'`).join(",");
            filterParts.push(`ToFilterObjectUserId in (${userList})`);
        }
        const filter = filterParts.join(" and ");
        const encodedFilter = encodeURIComponent(filter);
        const endpoint = `/api/reporting/interactions?$filter=${encodedFilter}&$orderby=InteractionStartDateUtc asc&$skip=${skip}&$top=${top}`;
        const response = await get(endpoint);
        return response.data || [];
    };
    return {
        interactions,
        loading,
        error,
        lastFetchCount,
        search,
    };
};
//# sourceMappingURL=useCallFinder.js.map